import { Popover } from '@taxbit-private/cosmic'
import { useCallback, useMemo, useEffect, useRef } from 'react'
import { useMatch } from 'react-router-dom'

import {
  useFetchGcDagRuns,
  useFetchGcDagNextRun,
  useFetchPendingChanges,
} from 'hooks/data/gc-dags/gcDagHooks'
import { usePrevious } from 'hooks/utils/usePrevious'
import useAppStore from 'store/useAppStore'

import {
  RefreshButton,
  RefreshButtonWithBlueStatusBubble,
  RefreshButtonWithOrangeStatusBubble,
  RefreshButtonWithRedStatusBubble,
} from './DataStatusButtons'
import DataStatusPopoverContent from './DataStatusPopoverContent'
import {
  getActiveDagRun,
  getMostRecentFailedDagRun,
  getLastSuccessfulDagRun,
  checkHasPendingChanges,
  checkIsFailedState,
} from './dataStatusUtils'

const DATA_STATUS_ERROR_TOAST_MESSAGE =
  'There was an error loading data status. Refresh the page to try again.'

/**
 * Poll the dag runs in the background so the status badge reflects a newly
 * triggered run (and its completion) without the popover being open or a page
 * refresh. Previously polling only ran while the popover was mounted.
 */
const DATA_STATUS_POLL_INTERVAL_MS = 30_000

const DataStatus = () => {
  const addCosmicToast = useAppStore((store) => store.addCosmicToast)
  const hasShownErrorToastRef = useRef(false)
  const match = useMatch('/app/:topLevel/*')
  const topLevelSegment = match?.params.topLevel ?? ''
  const previousTopLevelSegment = usePrevious(topLevelSegment)

  const {
    data: dagRunsData,
    isLoading: isDagRunsLoading,
    isError: isDagRunsError,
    refetch: refetchDagRuns,
  } = useFetchGcDagRuns({ refetchInterval: DATA_STATUS_POLL_INTERVAL_MS })
  const {
    data: pendingChangesData,
    isLoading: isPendingChangesLoading,
    isError: isPendingChangesError,
    refetch: refetchPendingChanges,
  } = useFetchPendingChanges()
  const {
    data: nextRunData,
    isLoading: isNextRunLoading,
    isError: isNextRunError,
    refetch: refetchNextRun,
  } = useFetchGcDagNextRun()

  const isLoading = isDagRunsLoading || isPendingChangesLoading || isNextRunLoading
  const isError = isDagRunsError || isPendingChangesError || isNextRunError
  const isButtonDisabled = isLoading || isError

  useEffect(() => {
    if (isError) {
      if (!hasShownErrorToastRef.current) {
        hasShownErrorToastRef.current = true
        addCosmicToast({
          variant: 'danger',
          message: DATA_STATUS_ERROR_TOAST_MESSAGE,
        })
      }
    } else {
      hasShownErrorToastRef.current = false
    }
  }, [isError, addCosmicToast])

  useEffect(() => {
    if (previousTopLevelSegment !== undefined && topLevelSegment !== previousTopLevelSegment) {
      void refetchDagRuns()
      void refetchPendingChanges()
      void refetchNextRun()
    }
  }, [
    topLevelSegment,
    previousTopLevelSegment,
    refetchDagRuns,
    refetchPendingChanges,
    refetchNextRun,
  ])

  const activeDagRun = useMemo(() => getActiveDagRun(dagRunsData?.dag_runs), [dagRunsData])

  const mostRecentFailedDagRun = useMemo(
    () => getMostRecentFailedDagRun(dagRunsData?.dag_runs),
    [dagRunsData]
  )

  const lastSuccessfulDagRun = useMemo(
    () => getLastSuccessfulDagRun(dagRunsData?.dag_runs),
    [dagRunsData]
  )

  const isFailedState = useMemo(
    () => checkIsFailedState(mostRecentFailedDagRun, lastSuccessfulDagRun),
    [mostRecentFailedDagRun, lastSuccessfulDagRun]
  )

  const hasPendingChanges = useMemo(
    () => checkHasPendingChanges(pendingChangesData),
    [pendingChangesData]
  )

  const iconName = useMemo(() => {
    if (activeDagRun || isFailedState || hasPendingChanges) {
      return 'refresh-with-status-bubble'
    }
    return 'refresh'
  }, [activeDagRun, isFailedState, hasPendingChanges])

  const ButtonComponent = useMemo(() => {
    if (activeDagRun) {
      return RefreshButtonWithBlueStatusBubble
    }
    if (isFailedState) {
      return RefreshButtonWithRedStatusBubble
    }
    if (hasPendingChanges) {
      return RefreshButtonWithOrangeStatusBubble
    }
    return RefreshButton
  }, [activeDagRun, isFailedState, hasPendingChanges])

  /**
   * SWR pattern: When the popover button is clicked, trigger background refetches.
   * Cached data is shown immediately while fresh data is fetched.
   */
  const handleMenuToggle = useCallback(
    (isMenuOpen: boolean) => {
      if (isMenuOpen) {
        void refetchDagRuns()
        void refetchPendingChanges()
        void refetchNextRun()
      }
    },
    [refetchDagRuns, refetchPendingChanges, refetchNextRun]
  )

  return (
    <Popover
      contents={
        <DataStatusPopoverContent
          dagRunsData={dagRunsData}
          pendingChangesData={pendingChangesData}
          nextRunData={nextRunData}
        />
      }
      minMenuWidth="xl"
      maxMenuWidth="xl"
      onMenuToggle={handleMenuToggle}
      targetButtonProps={{
        as: ButtonComponent,
        iconName,
        trackingId: 'data-status-trigger-button',
        variant: 'white',
        isDisabled: isButtonDisabled,
      }}
    />
  )
}

export default DataStatus
